// rtk query
import { Capacitor } from '@capacitor/core';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

// sentry
import * as Sentry from '@sentry/react';

// store
import { token } from 'store/slices/token';

export const apiUrl =
  import.meta.env.REACT_APP_API_URL || 'http://localhost:8085';
/** Where every v1 endpoint, REST or socket, is addressed from. */
export const apiV1Url = `${apiUrl}/v1/`;
export const kindeEnvironmentUrl =
  import.meta.env.REACT_APP_KINDE_ENVIRONMENT_URL || '';
export const kindeClientId = import.meta.env.REACT_APP_KINDE_CLIENT_ID || '';
export const sanityProjectId = 'x7r1t5o8';
export const sanityDataset = 'production';

/**
 * ApiExtraOptions are an endpoint's `extraOptions`. `expectedStatuses` lists
 * error statuses the endpoint handles as a normal outcome, such as a 404 for
 * something that doesn't exist yet, so they aren't reported to Sentry.
 */
export interface ApiExtraOptions {
  expectedStatuses?: string[];
}

// Statuses no endpoint needs reported.
const nonActionableStatuses = ['401', '409', '410'];

/**
 * isReportableApiError reports whether a failed request should be reported
 * to Sentry.
 */
export const isReportableApiError = (
  status: string,
  isOffline: boolean,
  extraOptions?: ApiExtraOptions,
) =>
  !isOffline &&
  !nonActionableStatuses.includes(status) &&
  !(extraOptions?.expectedStatuses ?? []).includes(status);

/**
 * baseQueryWithErrorHandling is a wrapper around fetchBaseQuery that logs
 * errors to Sentry.
 */
const baseQueryWithErrorHandling = async (
  args: any,
  api: any,
  extraOptions: any,
) => {
  const baseQuery = fetchBaseQuery({
    baseUrl: apiV1Url,
    prepareHeaders: async (headers) => {
      const accessToken = await token.getToken()();
      if (accessToken) {
        headers.set('Authorization', `Bearer ${accessToken}`);
      } else {
        // ENG-3071: Track when getToken() returns empty — this means the
        // Kinde SDK could not provide a token (expired + refresh failed,
        // or user not authenticated). The request will go out without auth
        // and likely receive a 401.
        Sentry.addBreadcrumb({
          category: 'auth',
          message: 'getToken returned empty — request will have no auth header',
          level: 'warning',
          data: {
            url: typeof args === 'string' ? args : (args?.url ?? 'unknown'),
          },
        });
      }

      // Add metadata to request headers
      headers.set(
        'X-Build-Environment',
        import.meta.env.REACT_APP_BUILD_ENVIRONMENT || 'unknown',
      );
      headers.set(
        'X-Build-Version',
        import.meta.env.REACT_APP_BUILD_VERSION || 'unknown',
      );
      headers.set(
        'X-Build-Hash',
        import.meta.env.REACT_APP_BUILD_HASH || 'unknown',
      );
      headers.set('X-Platform', Capacitor.getPlatform() || 'unknown');

      return headers;
    },
    credentials: 'include',
  });
  const result = await baseQuery(args, api, extraOptions);

  if (result.error) {
    const error = result.error;
    const status =
      'originalStatus' in error
        ? String(error.originalStatus)
        : String(error.status);
    const endpoint = typeof args === 'string' ? args : (args?.url ?? 'unknown');

    const isOffline = error.status === 'FETCH_ERROR' && !navigator.onLine;
    if (isReportableApiError(status, isOffline, extraOptions)) {
      const dataMessage =
        error.data && typeof error.data === 'object' && 'message' in error.data
          ? (error.data as { message: string }).message
          : typeof error.data === 'string'
            ? error.data
            : undefined;

      const message = `API ${status}: ${endpoint}${dataMessage ? ` — ${dataMessage}` : ''}`;
      const sentryError = new Error(message);
      sentryError.name = 'APIError';

      const responseHeaders = result.meta?.response?.headers;
      const requestId = responseHeaders?.get('X-Request-ID');
      const traceId = responseHeaders?.get('X-Trace-ID');

      Sentry.withScope((scope) => {
        scope.setTag('api.status', status);
        scope.setTag('api.endpoint', endpoint);
        if (requestId) scope.setTag('api.requestId', requestId);
        if (traceId) scope.setTag('api.traceId', traceId);
        scope.setFingerprint(['api-error', status, endpoint]);
        scope.setExtra('api.response', error.data);
        if (requestId) scope.setExtra('api.requestId', requestId);
        if (traceId) scope.setExtra('api.traceId', traceId);
        Sentry.captureException(sentryError);
      });
    }
  }

  return result;
};

export const TAG_TYPES = [
  'Accounts',
  'AccountRoles',
  'ActionPlanCategory',
  'ActionPlan',
  'ActionPlanFeedback',
  'ArticleBookmarks',
  'CareCircles',
  'CareProfileAnswers',
  'CareProfileCategories',
  'CarePlan',
  'Chat',
  'InvitedMembers',
  'OrganizationMembers',
  'Organizations',
  'ReferringPartners',
  'Personalization',
  'Relationships',
  'CareHomeShortlist',
  'CareHomeShortlistData',
  'AdminCareHomeShortlist',
  'AdminCareHomeShortlistData',
  'CareStage',
  'CareRecipientSummaries',
  'ServiceAccounts',
  'Triage',
  'Eligibilities',
  'Organise',
  'OrganiseDocument',
  'OrganiseDocumentTags',
  'PartnerProductCodes',
  'Notification',
  'Shortlists',
  'ProviderNotes',
  'Caseload',
  'InferenceExtraction',
  'IntakeSummary',
  'ChatSession',
  'Backtest',
  'EvaluationSource',
] as const;

export const api = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithErrorHandling,
  endpoints: () => ({}),
  tagTypes: TAG_TYPES,
});

export default api;
